import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	CarrierDTOResponse,
	DATE_YMD,
	DropdownItem,
	Owner,
	ProjectDTO,
	TASK_STATUS_UUID,
	TaskTable,
	TaskUpdateRequest,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SearchPipe } from '@shared/search.pipe';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';

@Component({
	selector: 'app-task-actions',
	templateUrl: './task-actions.component.html',
	styleUrls: ['./task-actions-dialog.template.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class TaskActionsComponent implements OnInit, OnChanges {
	@Input() public disabled: boolean = true;
	@Input() public selectedTasks: Array<TaskTable> = [];
	@Input() public projectId: string = null;

	@Output() public updateTasksEvent: EventEmitter<TaskUpdateRequest> = new EventEmitter();
	@Output() public deleteMultipleTasks: EventEmitter<any> = new EventEmitter();

	taskStatusList: DropdownItem[];
	request: TaskUpdateRequest = {
		taskIds: [],
		statusId: null,
		dueDate: null,
		projectId: null,
		carrierId: null,
		assigneeId: null,
	};
	accountId: number = null;
	isAdminOrOwner: boolean = false;

	constructor(
		private utilService: UtilService,
		public dialog: MatDialog,
		public cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService
	) {
		this.taskStatusList = Object.keys(TASK_STATUS_UUID).map((key) => {
			return {
				label: TASK_STATUS_UUID[key],
				value: key,
			};
		});
		this.accountId = this.cargochief.ACCOUNT_ID;
	}

	ngOnInit() {
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
	}

	updateStatus(statusId: string) {
		this.request.statusId = statusId;
		this.fireSegmentEvent(
			`Status Changed to: ${TASK_STATUS_UUID[statusId]}`,
			null,
			SegmentEventsConstants.user_updated_tasks
		);
		this.emitUpdateTaskEvent();
	}

	ngOnChanges() {
		if (!this.utilService.isNullOrUndefined(this.selectedTasks) && this.selectedTasks.length > 0) {
			this.request.taskIds = this.selectedTasks.map((task) => task.id);
		}
	}

	emitUpdateTaskEvent() {
		this.updateTasksEvent.emit(this.request);
	}

	openTaskUpdateModal({
		changeDueDate,
		changeProject,
		changeCarrier,
		title,
		changeAssignee,
	}: Partial<UpdateDialogInputData>): void {
		const dialogRef = this.dialog.open<TaskActionsDialogComponent, UpdateDialogInputData>(
			TaskActionsDialogComponent,
			{
				width: '400px',
				data: {
					changeDueDate,
					changeProject,
					changeCarrier,
					title,
					changeAssignee,
					accountId: this.accountId,
					projectId: this.projectId,
				},
			}
		);

		dialogRef.afterClosed().subscribe((result: UpdateDialogOutputData) => {
			if (!!result) {
				this.request = { ...this.request, ...result };
				const action = result.projectId
					? 'Project Assigned'
					: result.dueDate
					? 'Due Date Changed'
					: result.carrierId
					? 'Carrier Changed'
					: result.assigneeId
					? 'Assignee Changed'
					: '';
				this.fireSegmentEvent(action, result, SegmentEventsConstants.user_updated_tasks);
				this.emitUpdateTaskEvent();
			}
		});
	}

	deleteSelectedTasks() {
		this.fireSegmentEvent('Tasks Deleted', null, SegmentEventsConstants.user_deleted_tasks);
		this.deleteMultipleTasks.emit();
	}

	fireSegmentEvent(action: string, request: any = null, event: string) {
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
			action: action,
			taskIds: this.selectedTasks.map(({ id }) => id).join(', '),
			request: request,
		};
		this.segmentWrapper.fireSegmentEvent(event, payload);
	}
}

@Component({
	selector: 'app-task-actions-dialog',
	templateUrl: 'task-actions-dialog.template.html',
	styleUrls: ['./task-actions-dialog.template.scss'],
	providers: [SearchPipe],
})
export class TaskActionsDialogComponent implements OnInit {
	dueDate = new UntypedFormControl(null, Validators.required);
	minimumDueDate = new Date();
	outputData: UpdateDialogOutputData = {
		dueDate: null,
		projectId: null,
		carrierId: null,
		assigneeId: null,
		assigneeName: null,
	};
	selectedDueDate = null;

	constructor(
		public dialogRef: MatDialogRef<TaskActionsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: UpdateDialogInputData,
		public utilService: UtilService
	) {}

	closeDialog(): void {
		this.dialogRef.close();
	}

	ngOnInit() {}

	updateDueDate(dateObject: MatDatepickerInputEvent<Date>) {
		this.outputData.dueDate = DATE_YMD(dateObject.value);
	}

	updateTasks() {
		this.dialogRef.close(this.outputData);
	}

	clear(element: UntypedFormControl) {
		element.reset();
	}

	disableUpdate() {
		if (this.data.changeDueDate) {
			return this.dueDate.invalid;
		} else if (this.data.changeProject) {
			return this.utilService.isNullOrUndefined(this.outputData.projectId);
		} else if (this.data.changeCarrier) {
			return this.utilService.isNullOrUndefined(this.outputData.carrierId);
		} else if (this.data.changeAssignee) {
			return (
				this.utilService.isNullOrUndefined(this.outputData.assigneeName) ||
				this.utilService.isNullOrUndefined(this.outputData.assigneeId)
			);
		}
	}

	projectSelected(project: ProjectDTO) {
		if (!this.utilService.isNullOrUndefined(project)) {
			this.outputData.projectId = project.id;
		} else {
			this.outputData.projectId = null;
		}
	}

	carrierSelected(carrier: CarrierDTOResponse) {
		if (!this.utilService.isNullOrUndefined(carrier)) {
			this.outputData.carrierId = carrier.id;
		} else {
			this.outputData.carrierId = null;
		}
	}

	setAssignee(user: Owner) {
		if (!this.utilService.isNullOrUndefined(user)) {
			this.outputData.assigneeId = user.userId;
			this.outputData.assigneeName = user.name;
		} else {
			this.outputData.assigneeId = null;
			this.outputData.assigneeName = null;
		}
	}
}

interface UpdateDialogInputData {
	changeDueDate: boolean;
	changeProject: boolean;
	changeCarrier: boolean;
	title: string;
	accountId: number;
	changeAssignee: boolean;
	projectId: string;
}

interface UpdateDialogOutputData {
	dueDate: string;
	projectId: string;
	carrierId: string;
	assigneeId: number;
	assigneeName: string;
}
